import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Form, Modal } from 'react-bootstrap';
import AdminLayout from '../layouts/AdminLayout';
import CreateTeamModal from './CreateTeamModal';
import EditTeamModal from './EditTeamModal';
import TeamMembersModal from './TeamMembersModal';
import api from '../../services/api';
import './Users.css';

interface TeamMember {
  id: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
  username: string;
  role: string;
  created_at: string;
  can_edit_buoni?: boolean;
  can_delete_buoni?: boolean;
  team_members?: TeamMember[];
  teamMembers?: TeamMember[];
}

type DeleteTarget =
  | { type: 'user'; teamId: number; teamName: string }
  | { type: 'operator'; teamId: number; memberId: number; memberName: string };

const Users: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState<Team | null>(null);
  /** Team per cui è in corso il salvataggio permessi dalla card (per disabilitare checkbox) */
  const [savingPermessiForTeamId, setSavingPermessiForTeamId] = useState<number | null>(null);
  /** Card in cui è aperto il form "Aggiungi operatore" (id team) */
  const [addingOperatorForTeamId, setAddingOperatorForTeamId] = useState<number | null>(null);
  const [newOperatorName, setNewOperatorName] = useState('');
  const [addOperatorLoading, setAddOperatorLoading] = useState(false);
  const [addOperatorError, setAddOperatorError] = useState('');
  /** Operatore in modifica inline (teamId, memberId) */
  const [editingOperator, setEditingOperator] = useState<{ teamId: number; memberId: number } | null>(null);
  const [editingOperatorName, setEditingOperatorName] = useState('');
  const [editOperatorLoading, setEditOperatorLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/teams');
      console.log('Team caricati dal backend:', response.data);
      // Normalizza i membri del team (potrebbero essere teamMembers o team_members)
      const normalizedTeams = (response.data || []).map((team: any) => ({
        ...team,
        teamMembers: team.teamMembers || team.team_members || [],
      }));
      setTeams(normalizedTeams);
    } catch (error) {
      console.error('Errore nel caricamento dei team:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserClick = (team: Team) => {
    setDeleteTarget({ type: 'user', teamId: team.id, teamName: team.username });
  };

  const handleDeleteOperatorClick = (teamId: number, memberId: number, memberName: string) => {
    setDeleteTarget({ type: 'operator', teamId, memberId, memberName });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'user') {
        await api.delete(`/admin/teams/${deleteTarget.teamId}`);
      } else {
        await api.delete(`/admin/teams/${deleteTarget.teamId}/members/${deleteTarget.memberId}`);
      }
      setDeleteTarget(null);
      loadTeams();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      alert('Errore nell\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTogglePermesso = async (team: Team, field: 'can_edit_buoni' | 'can_delete_buoni', newValue: boolean) => {
    setSavingPermessiForTeamId(team.id);
    try {
      await api.put(`/admin/teams/${team.id}`, {
        username: team.username,
        can_edit_buoni: field === 'can_edit_buoni' ? newValue : !!team.can_edit_buoni,
        can_delete_buoni: field === 'can_delete_buoni' ? newValue : !!team.can_delete_buoni,
      });
      await loadTeams();
    } catch (err) {
      console.error('Errore aggiornamento permesso:', err);
    } finally {
      setSavingPermessiForTeamId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  const getTeamMembers = (team: Team): TeamMember[] => {
    return team.teamMembers || team.team_members || [];
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2) || '?';
  };

  const handleStartAddOperator = (teamId: number) => {
    setAddingOperatorForTeamId(teamId);
    setNewOperatorName('');
    setAddOperatorError('');
  };

  const handleCancelAddOperator = () => {
    setAddingOperatorForTeamId(null);
    setNewOperatorName('');
    setAddOperatorError('');
  };

  const handleAddOperatorSubmit = async (e: React.FormEvent, teamId: number) => {
    e.preventDefault();
    setAddOperatorError('');
    const name = newOperatorName.trim();
    if (!name) {
      setAddOperatorError('Inserisci il nome dell\'operatore');
      return;
    }
    setAddOperatorLoading(true);
    try {
      await api.post(`/admin/teams/${teamId}/members`, { name });
      setAddingOperatorForTeamId(null);
      setNewOperatorName('');
      loadTeams();
    } catch (err: any) {
      setAddOperatorError(err.response?.data?.message || err.response?.data?.errors?.name?.[0] || 'Errore nell\'aggiunta dell\'operatore');
    } finally {
      setAddOperatorLoading(false);
    }
  };

  const handleStartEditOperator = (teamId: number, memberId: number, currentName: string) => {
    setEditingOperator({ teamId, memberId });
    setEditingOperatorName(currentName);
  };

  const handleCancelEditOperator = () => {
    setEditingOperator(null);
    setEditingOperatorName('');
  };

  const handleSaveEditOperator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOperator) return;
    const name = editingOperatorName.trim();
    if (!name) return;
    setEditOperatorLoading(true);
    try {
      await api.put(`/admin/teams/${editingOperator.teamId}/members/${editingOperator.memberId}`, { name });
      setEditingOperator(null);
      setEditingOperatorName('');
      loadTeams();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Errore durante la modifica');
    } finally {
      setEditOperatorLoading(false);
    }
  };

  const handleEdit = async (team: Team) => {
    try {
      // Carica i dati completi del team con i membri
      const response = await api.get(`/admin/teams/${team.id}`);
      setTeamToEdit({
        ...response.data,
        teamMembers: response.data.teamMembers || response.data.team_members || [],
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Errore nel caricamento del team:', error);
      setTeamToEdit(team);
      setShowEditModal(true);
    }
  };

  return (
    <AdminLayout activeSection="utenti">
      <div className="admin-content users-page-content">
        {/* Header - Figma: titolo + sottotitolo a sinistra, Crea Utente a destra */}
        <div className="content-header">
          <div>
            <h1 className="content-title">Utenti</h1>
            <p className="content-subtitle">Gestisci gli utenti</p>
          </div>
          <button
            type="button"
            className="users-btn-create"
            onClick={() => setShowCreateModal(true)}
          >
            <span className="users-btn-create-icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.16699 10H15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            Crea Utente
          </button>
        </div>

        {/* Users Cards */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Caricamento...</span>
            </div>
          </div>
        ) : teams.length === 0 ? (
          <Card className="shadow-sm">
            <Card.Body className="text-center py-5 text-muted">
              Nessun utente trovato
            </Card.Body>
          </Card>
        ) : (
          <Row xs={1} md={2} xl={2} className="g-4">
            {teams.map((team) => {
              const members = getTeamMembers(team);
              const isLoadingMembers = false; // non più usato: membri caricati con i team

              return (
                <Col key={team.id}>
                  <div className="user-card shadow-sm">
                    {/* Card header */}
                    <div className="user-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-3">
                        <div className="user-card-avatar">
                          {getInitials(team.username)}
                        </div>
                        <div>
                          <h2 className="user-card-title">{team.username}</h2>
                          <p className="user-card-subtitle">Dettagli utente</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="user-card-btn"
                          onClick={() => handleEdit(team)}
                        >
                          Modifica
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="user-card-btn user-card-btn-danger"
                          onClick={() => handleDeleteUserClick(team)}
                        >
                          Elimina
                        </Button>
                      </div>
                    </div>

                    {/* Card body - uguale al design: due colonne Operatori | Permessi */}
                    <div className="user-card-body">
                      <div className="row g-3 g-lg-4 user-card-sections-row justify-content-center">
                        {/* Operatori */}
                        <div className="col-12 col-lg-6">
                          <div className="user-card-section user-card-section-operatori">
                            <div className="user-card-section-head d-flex justify-content-between align-items-center">
                              <h3 className="user-card-section-title mb-0">Operatori</h3>
                              {addingOperatorForTeamId !== team.id && (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-decoration-none user-card-link"
                                  onClick={() => handleStartAddOperator(team.id)}
                                >
                                  + Aggiungi Operatore
                                </button>
                              )}
                            </div>
                            {addingOperatorForTeamId === team.id && (
                              <Form
                                onSubmit={(e) => handleAddOperatorSubmit(e, team.id)}
                                className="user-card-add-operator-form"
                              >
                                {addOperatorError && (
                                  <div className="small text-danger mb-2">{addOperatorError}</div>
                                )}
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                  <Form.Control
                                    type="text"
                                    placeholder="Nome operatore"
                                    value={newOperatorName}
                                    onChange={(e) => setNewOperatorName(e.target.value)}
                                    disabled={addOperatorLoading}
                                    className="user-card-add-operator-input"
                                    autoFocus
                                  />
                                  <div className="d-flex gap-2">
                                    <Button
                                      type="submit"
                                      size="sm"
                                      className="user-card-btn-add-operator"
                                      disabled={addOperatorLoading}
                                    >
                                      {addOperatorLoading ? 'Aggiunta...' : 'Aggiungi'}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={handleCancelAddOperator}
                                      disabled={addOperatorLoading}
                                    >
                                      Annulla
                                    </Button>
                                  </div>
                                </div>
                              </Form>
                            )}
                            <div className="user-card-operators">
                              {members.length > 0 ? (
                                members.map((member, idx) => {
                                  const isEditing = editingOperator?.teamId === team.id && editingOperator?.memberId === member.id;
                                  return (
                                    <div key={member.id} className="user-card-operator-row">
                                      <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                                        <span
                                          className={`user-card-operator-avatar ${idx % 2 === 0 ? 'user-card-operator-avatar-blue' : 'user-card-operator-avatar-purple'}`}
                                        >
                                          {(isEditing ? editingOperatorName : member.name).charAt(0).toUpperCase()}
                                        </span>
                                        {isEditing ? (
                                          <Form
                                            onSubmit={handleSaveEditOperator}
                                            className="d-flex align-items-center gap-2 flex-grow-1 min-w-0"
                                          >
                                            <Form.Control
                                              type="text"
                                              value={editingOperatorName}
                                              onChange={(e) => setEditingOperatorName(e.target.value)}
                                              disabled={editOperatorLoading}
                                              className="user-card-operator-edit-input"
                                              autoFocus
                                              size="sm"
                                            />
                                            <Button type="submit" size="sm" className="user-card-btn-add-operator" disabled={editOperatorLoading}>
                                              {editOperatorLoading ? '...' : 'Salva'}
                                            </Button>
                                            <Button type="button" variant="outline-secondary" size="sm" onClick={handleCancelEditOperator} disabled={editOperatorLoading}>
                                              Annulla
                                            </Button>
                                          </Form>
                                        ) : (
                                          <>
                                            <span className="user-card-operator-name">{member.name}</span>
                                            <div className="user-card-operator-actions ms-auto">
                                              <button
                                                type="button"
                                                className="user-card-operator-icon-btn"
                                                onClick={() => handleStartEditOperator(team.id, member.id, member.name)}
                                                aria-label="Modifica nome"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                  <path d="M12.2504 1.75015C12.5156 1.48493 12.8753 1.33594 13.2504 1.33594C13.6255 1.33594 13.9852 1.48493 14.2504 1.75015C14.5156 2.01537 14.6646 2.37508 14.6646 2.75015C14.6646 3.12522 14.5156 3.48493 14.2504 3.75015L8.24172 9.75948C8.08342 9.91765 7.88786 10.0334 7.67305 10.0962L5.75772 10.6562C5.70036 10.6729 5.63955 10.6739 5.58166 10.6591C5.52377 10.6442 5.47094 10.6141 5.42869 10.5719C5.38643 10.5296 5.35631 10.4768 5.34148 10.4189C5.32665 10.361 5.32766 10.3002 5.34439 10.2428L5.90439 8.32748C5.96741 8.11285 6.08341 7.91752 6.24172 7.75948L12.2504 1.75015Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              </button>
                                              <button
                                                type="button"
                                                className="user-card-operator-icon-btn user-card-operator-icon-trash"
                                                aria-label="Elimina operatore"
                                                onClick={() => handleDeleteOperatorClick(team.id, member.id, member.name)}
                                              >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M2 4H14" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                  <path d="M12.6663 4V13.3333C12.6663 14 11.9997 14.6667 11.333 14.6667H4.66634C3.99967 14.6667 3.33301 14 3.33301 13.3333V4" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                  <path d="M5.33301 4.00016V2.66683C5.33301 2.00016 5.99967 1.3335 6.66634 1.3335H9.33301C9.99967 1.3335 10.6663 2.00016 10.6663 2.66683V4.00016" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                  <path d="M6.66699 7.3335V11.3335" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                  <path d="M9.33301 7.3335V11.3335" stroke="#5F6A7C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              </button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-muted small mb-0">Nessun operatore</p>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Permessi - 2 checkbox distinte, salvataggio su backend al toggle */}
                        <div className="col-12 col-lg-6">
                          <div className="user-card-section user-card-section-permessi">
                            <h3 className="user-card-section-title">Permessi</h3>
                            <div className="user-card-permissions-list">
                              <label className="user-card-permission-row">
                                <Form.Check
                                  type="checkbox"
                                  checked={!!team.can_delete_buoni}
                                  disabled={savingPermessiForTeamId === team.id}
                                  onChange={() => handleTogglePermesso(team, 'can_delete_buoni', !team.can_delete_buoni)}
                                  className="user-card-permission-cb"
                                />
                                <span className="user-card-permission-label">Elimina Buono</span>
                              </label>
                              <label className="user-card-permission-row">
                                <Form.Check
                                  type="checkbox"
                                  checked={!!team.can_edit_buoni}
                                  disabled={savingPermessiForTeamId === team.id}
                                  onChange={() => handleTogglePermesso(team, 'can_edit_buoni', !team.can_edit_buoni)}
                                  className="user-card-permission-cb"
                                />
                                <span className="user-card-permission-label">Modifica Buono</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Pagination - visibile solo quando ci sono più di 4 utenti */}
        {teams.length > 4 && (
          <div className="users-page-pagination">
            <div className="pagination-custom">
              <button className="pagination-btn">‹</button>
              <button className="pagination-btn active">1</button>
              <button className="pagination-btn">›</button>
              <span className="ms-3 text-muted">Pagina 1 di 1</span>
            </div>
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      <CreateTeamModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={loadTeams}
      />

      {/* Members Modal */}
      <TeamMembersModal
        show={showMembersModal}
        onHide={() => {
          setShowMembersModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onUpdate={loadTeams}
      />

      {/* Edit Team Modal */}
      <EditTeamModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setTeamToEdit(null);
        }}
        onSuccess={loadTeams}
        team={teamToEdit}
      />

      {/* Confirm Delete Modal (utente/operatore) */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
        className="confirm-delete-modal"
      >
        <div className="confirm-delete-box">
          <div className="confirm-delete-header">
            <h2 className="confirm-delete-title">
              {deleteTarget?.type === 'operator'
                ? 'Sei sicuro di voler eliminare questo operatore?'
                : 'Sei sicuro di voler eliminare questo utente?'}
            </h2>
            <button
              type="button"
              className="confirm-delete-close"
              onClick={() => setDeleteTarget(null)}
              aria-label="Chiudi"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 2l12 12M14 2L2 14" />
              </svg>
            </button>
          </div>

          <div className="confirm-delete-footer">
            <Button
              type="button"
              variant="light"
              className="confirm-delete-btn-cancel"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteLoading}
            >
              Annulla
            </Button>
            <Button
              type="button"
              className="confirm-delete-btn-confirm"
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default Users;
